import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UserPlus, Trash2, UserCog, Loader2, KeyRound, Mail } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const token = () => localStorage.getItem("veloclin_token");

export const SecretariesAdmin = () => {
  const [secs, setSecs] = useState([]);
  const [isMax, setIsMax] = useState(false);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });

  const load = async () => {
    const t = token();
    if (!t) { setLoading(false); return; }
    try {
      const r = await fetch(`${API}/api/clinic/secretaries`, { headers: { Authorization: `Bearer ${t}` } });
      const j = await r.json();
      if (r.ok) { setSecs(j.secretaries); setIsMax(j.is_max); }
    } catch (e) { /* noop */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const save = async () => {
    if (!form.name.trim() || !form.email.trim()) return toast.error("Informe nome e e-mail.");
    if (form.password.length < 6) return toast.error("A senha deve ter ao menos 6 caracteres.");
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/clinic/secretaries`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token()}` },
        body: JSON.stringify(form),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || "Erro ao cadastrar");
      setSecs((prev) => [...prev, j.secretary]);
      toast.success("Secretária cadastrada!", { description: `${form.name} já pode acessar com o login criado.` });
      setForm({ name: "", email: "", password: "", phone: "" });
      setOpen(false);
    } catch (e) {
      toast.error("Não foi possível cadastrar", { description: e.message });
    }
    setSaving(false);
  };

  const remove = async (id) => {
    try {
      const r = await fetch(`${API}/api/clinic/secretaries/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token()}` } });
      if (!r.ok) throw new Error();
      setSecs((prev) => prev.filter((s) => s.id !== id));
      toast.success("Secretária removida.");
    } catch { toast.error("Não foi possível remover."); }
  };

  if (!token()) {
    return (
      <div data-testid="secretaries-no-token" className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-center text-sm text-amber-800">
        Entre com uma conta de clínica para gerenciar as secretárias com login e senha.
      </div>
    );
  }

  return (
    <div data-testid="secretaries-admin">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-base font-bold text-slate-900">Equipe de Recepção</h2>
          <p className="text-xs text-slate-500">
            {secs.length} secretária{secs.length !== 1 ? "s" : ""} · {isMax ? "cadastro ilimitado (Plano Max)" : "até 1 no Plano Basic"}
          </p>
        </div>
        <button
          data-testid="new-secretary-btn"
          onClick={() => setOpen(true)}
          className="flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-[0_2px_8px_rgba(37,99,235,0.3)] transition-colors hover:bg-blue-700"
        >
          <UserPlus className="h-4 w-4" /> Nova Secretária
        </button>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200/70 bg-white shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-10 text-sm text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> Carregando…</div>
        ) : secs.length === 0 ? (
          <p className="py-10 text-center text-sm text-slate-400">Nenhuma secretária cadastrada ainda.</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {secs.map((s) => (
              <div key={s.id} data-testid={`secretary-row-${s.id}`} className="flex items-center gap-3 px-5 py-3.5">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-blue-700"><UserCog className="h-5 w-5" /></span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-bold text-slate-800">{s.name}</p>
                  <p className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-slate-500">
                    <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" /> {s.email}</span>
                    {s.phone && <span>{s.phone}</span>}
                    <span className="inline-flex items-center gap-1 text-emerald-600"><KeyRound className="h-3 w-3" /> acesso ativo</span>
                  </p>
                </div>
                <button data-testid={`secretary-delete-${s.id}`} onClick={() => remove(s.id)} className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-rose-50 hover:text-rose-600">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="secretary-modal">
          <DialogHeader>
            <DialogTitle>Nova secretária</DialogTitle>
            <DialogDescription>Crie um acesso de login e senha. A secretária entrará com permissões limitadas à recepção — sem acesso a informações do sócio.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label className="text-xs font-semibold text-slate-600">Nome</Label><Input data-testid="secretary-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" placeholder="Nome completo" /></div>
            <div><Label className="text-xs font-semibold text-slate-600">E-mail (login)</Label><Input data-testid="secretary-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" placeholder="secretaria@clinica.com" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-semibold text-slate-600">Senha</Label><Input data-testid="secretary-password" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5" placeholder="mín. 6 caracteres" /></div>
              <div><Label className="text-xs font-semibold text-slate-600">Telefone</Label><Input data-testid="secretary-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" placeholder="(11) 90000-0000" /></div>
            </div>
          </div>
          <DialogFooter>
            <button data-testid="secretary-save-btn" onClick={save} disabled={saving} className="flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 py-2.5 text-sm font-bold text-white hover:bg-blue-700 disabled:opacity-60">
              {saving ? <><Loader2 className="h-4 w-4 animate-spin" /> Salvando…</> : "Criar acesso"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
